import { t } from '@sd/i18n';
import type { GameEvent, GameState } from '@sd/sim';
import type { Viewer } from '../map/describe';
import { escapeHtml } from './format';
import {
  currentHint,
  emptyProgress,
  observe,
  type HintId,
  type HintTab,
  type TutorialProgress,
} from './tutorial';

/** Whether hints are switched off, kept per browser: a convenience, so a failure just means «on». */
const STORAGE_KEY = 'sd.hints.off';

export function hintsOff(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setHintsOff(off: boolean) {
  try {
    if (off) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the choice lasts for this match only.
  }
}

/**
 * The tutorial hint card at the foot of the map (#59): one hint, a button
 * to open the tab it is about, «Понятно» and «Выключить подсказки».
 */
export class HintCard {
  private readonly el: HTMLElement;
  private progress: TutorialProgress = emptyProgress();
  private off = hintsOff();
  private shown: HintId | null = null;
  private last = '';

  constructor(
    parent: HTMLElement,
    private readonly viewer: Viewer,
    private readonly openTab: (tab: HintTab) => void,
  ) {
    this.el = document.createElement('aside');
    this.el.className = 'sd-panel sd-hint-card';
    this.el.setAttribute('role', 'status');
    this.el.hidden = true;
    parent.append(this.el);
    this.el.addEventListener('click', (event) => {
      const action = (event.target as HTMLElement).closest<HTMLElement>('[data-action]')?.dataset
        .action;
      if (action === 'hint-tab') {
        const tab = (event.target as HTMLElement).closest<HTMLElement>('[data-tab]')?.dataset.tab;
        if (tab) this.openTab(tab as HintTab);
      } else if (action === 'hint-ok' && this.shown) {
        this.progress = {
          ...this.progress,
          dismissed: new Set([...this.progress.dismissed, this.shown]),
        };
        this.draw(null);
      } else if (action === 'hint-off') {
        this.setOff(true);
      }
    });
  }

  get isOff(): boolean {
    return this.off;
  }

  setOff(off: boolean) {
    this.off = off;
    setHintsOff(off);
    if (off) this.draw(null);
  }

  update(state: GameState, events: readonly GameEvent[]) {
    this.progress = observe(this.progress, events, this.viewer.me);
    // A dismissed crisis hint comes back with the next crisis.
    if (
      this.progress.dismissed.has('crisis') &&
      !state.crises.some((c) => c.warned.includes(this.viewer.me))
    ) {
      const dismissed = new Set(this.progress.dismissed);
      dismissed.delete('crisis');
      this.progress = { ...this.progress, dismissed };
    }
    this.draw(this.off ? null : currentHint(state, this.viewer, this.progress));
  }

  private draw(hint: ReturnType<typeof currentHint>) {
    const { locale } = this.viewer;
    this.shown = hint?.id ?? null;
    const html = hint
      ? `<div class="sd-hint-card__head"><b>${escapeHtml(hint.title)}</b>${hint.step ? `<span class="sd-label">${escapeHtml(hint.step)}</span>` : ''}</div>
        <p>${escapeHtml(hint.text)}</p>
        <div class="sd-buttons">
          ${hint.tab ? `<button class="sd-btn sd-btn--primary" type="button" data-action="hint-tab" data-tab="${hint.tab}">${t(locale, 'hint.open', { tab: t(locale, `tab.${hint.tab}`) })}</button>` : ''}
          <button class="sd-btn" type="button" data-action="hint-ok">${t(locale, 'hint.ok')}</button>
          <button class="sd-btn sd-hint-card__off" type="button" data-action="hint-off">${t(locale, 'hint.off')}</button>
        </div>`
      : '';
    if (html === this.last) return;
    this.last = html;
    this.el.innerHTML = html;
    this.el.hidden = !hint;
    this.el.dataset.hint = hint?.id ?? '';
  }

  dispose() {
    this.el.remove();
  }
}
